import type { ProgramAccount } from "@coral-xyz/anchor";
import { BN } from "@coral-xyz/anchor";
import type { PublicKey } from "@solana/web3.js";
import { convert, getUnixEpochSeconds, type MinimalTokenInfo } from "@stepfinance/common";
import type { FarmStatus, StepFarmVersion, StepRewardPool, StepRewardUser } from "@stepfinance/farms";

import type { MarketsContextState } from "../../../contexts/market";

const SECONDS_IN_DAY = new BN(86400);
const SECONDS_IN_YEAR = new BN(365 * 24 * 60 * 60);
const U64_MAX = new BN("ffffffffffffffff", 16);

const lastTimeRewardApplicable = (pool: StepRewardPool) => {
  return BN.min(new BN(getUnixEpochSeconds()), pool.rewardDurationEnd);
};

export const calculateRewardPerToken = (
  poolAccount: ProgramAccount<StepRewardPool>["account"],
  rewardPerTokenStored: BN,
  rewardRate: BN,
  rewardDecimals: number | undefined,
  vaultAmount: BN | undefined,
) => {
  if (!vaultAmount || vaultAmount.isZero() || rewardDecimals === undefined) {
    return rewardPerTokenStored;
  }
  const poolVersion = poolAccount.version;
  const rewardPerToken = rewardPerTokenStored.add(
    lastTimeRewardApplicable(poolAccount)
      .sub(poolAccount.lastUpdateTime)
      .mul(rewardRate)
      .mul(U64_MAX)
      .div(poolVersion["v2"] ? SECONDS_IN_YEAR : new BN(1))
      .div(vaultAmount),
  );

  return rewardPerToken;
};
export const calculateUserRewardABalance = (
  userReward: ProgramAccount<StepRewardUser>["account"],
  rewardPerToken: BN,
) => {
  const rewardBalance = userReward.balanceStaked
    .mul(rewardPerToken.sub(userReward.rewardAPerTokenComplete))
    .div(U64_MAX)
    .add(userReward.rewardAPerTokenPending);
  return rewardBalance;
};
export const calculateUserRewardBBalance = (
  userReward: ProgramAccount<StepRewardUser>["account"],
  rewardPerToken: BN,
) => {
  const rewardBalance = userReward.balanceStaked
    .mul(rewardPerToken.sub(userReward.rewardBPerTokenComplete))
    .div(U64_MAX)
    .add(userReward.rewardBPerTokenPending);
  return rewardBalance;
};

interface RewardPoolAprArgs {
  farmVersion: StepFarmVersion;
  farmStatus: FarmStatus;
  rewardRate: BN;
  rewardMint: PublicKey;
  rewardMintInfo: MinimalTokenInfo | undefined;
  farmLiquidityInUSD: number;
  priceInUSD: MarketsContextState["priceInUSD"];
}

export const getRewardPoolApr = ({
  farmVersion,
  farmStatus,
  rewardRate,
  rewardMint,
  rewardMintInfo,
  farmLiquidityInUSD,
  priceInUSD,
}: RewardPoolAprArgs): number => {
  if (farmStatus === "ended" || farmStatus === "pending") {
    return 0;
  }
  return (
    ((convert(rewardRate.mul(farmVersion === 1 ? SECONDS_IN_DAY : new BN(1)), rewardMintInfo?.decimals) *
      (priceInUSD(rewardMint.toBase58()) ?? 0) *
      (farmVersion === 1 ? 365 : 1)) /
      Math.abs(farmLiquidityInUSD)) *
    100
  );
};
